// React Imports
import React, { useState, useEffect, useCallback } from 'react';
import { useMap, useMapEvents } from 'react-leaflet/hooks';

// Other Imports
import {
  calculateCenter,
  fillScreen,
  setSelectedSubregionNull,
} from './MyMap.js';
import configuration from '../data/config.json';
import '../styles/bottombar.css';

// Icon Imports
import { BiFullscreen } from 'react-icons/bi';
import {
  MdOutlineFullscreenExit,
  MdOutlineCenterFocusStrong,
} from 'react-icons/md';
import { BsPlayFill, BsPauseFill } from 'react-icons/bs';
import { AiFillStepForward, AiFillStepBackward } from 'react-icons/ai';

// ------------------- HELPER COMPONENTS -------------------

// Component that keeps track of the current frame
export const FrameCounter = ({
  animationStartTime,
  setLiveFrameNum,
  mainTL,
  isPaused,
  renderNumber,
}) => {
  var FPS;
  var runTime; // seconds
  var updateTime;
  var num_keyframes_current_render;
  var duration;
  if(renderNumber == 42 || renderNumber == 56){
    FPS = 10;
    runTime = 10;
    updateTime = 1000 / FPS;
    duration = 10000;
    num_keyframes_current_render = configuration.num_key_frames;
  }
  if(renderNumber == 14){
    num_keyframes_current_render = configuration.num_key_frames_render14;
    FPS = 10;
    runTime = 6.4;
    updateTime = 1000 / FPS;
    duration = 6400;
  } 

  useEffect(() => {
    const timer = setInterval(() => {
      // This if statement makes sure that the animation is playing
      // mainTL.current.options.opacity == 1 means the animation is visible and therefore playing
      if (
        !isPaused &&
        animationStartTime &&
        mainTL.current &&
        mainTL.current.options.opacity === 1
      ) {
        /*const elapsedSeconds =
          ((Date.now() - animationStartTime) / 1000) % runTime;
        setLiveFrameNum(Math.floor(elapsedSeconds * FPS));*/
        let msElapsed = (Date.now() - animationStartTime) % duration;
        setLiveFrameNum(
          Math.floor( (msElapsed / 1000) * FPS)
        );
      }
    }, updateTime); // Update roughly every frame

    return () => clearInterval(timer); // Cleanup on unmount
  });
};

const FullScreenBtn = ({ handle }) => {
  const handleOnClick = () => {
    if (handle.active) {
      handle.exit();
    } else {
      handle.enter();
    }
  };

  return (
    <div className="bttn fullscreen-btn" onClick={handleOnClick}>
      {!handle.active ? (
        <BiFullscreen className="icon" />
      ) : (
        <MdOutlineFullscreenExit className="icon" />
      )}
      <span
        className="tooltiptext tooltiptext-fs "
        style={{
          backgroundColor: configuration.tooltip_background_color,
          color: configuration.tooltip_text_color,
        }}
      >
        fullscreen
      </span>
    </div>
  );
};

// ------------------- MAIN COMPONENT -------------------
export const Bottombar = ({
  handle,
  sidebarState,
  setSidebar,
  setMasterTile,
  setCurrentPrompt,
  setSubregionMode,
  setFrameNum,
  frameNum,
  isPaused,
  setIsPaused,
  animationStartTime,
  sync_flag,
  mainTL,
  tilePreview,
  setLeadingFrameNum,
  setTilePreview,
  setListView,
  renderNumber,
}) => {
  const map = useMap();
  const [currentCoords, setCurrentCoords] = useState({ lat: 0, lng: 0 }); // The current mouse location
  const [liveFrameNum, setLiveFrameNum] = useState(0); // The frame number that is currently being displayed
  const [mouseOverSidebar, setMouseOverSidebar] = useState(false);

  // Helper functions
  const frameForward = useCallback(() => {
    if (!isPaused) {
      setIsPaused(1); // Pause on frame change
    }

    var num_keyframes_current_render;
    if(renderNumber == 42 || renderNumber == 56){
      num_keyframes_current_render = configuration.num_key_frames;
    }
    if(renderNumber == 14){
      num_keyframes_current_render = configuration.num_key_frames_render14;
    } 
    setLeadingFrameNum((frameNum + 1) % num_keyframes_current_render); // Loops arounds
  }, [isPaused, frameNum, renderNumber]);

  const frameBackward = useCallback(() => {
    if (!isPaused) {
      setIsPaused(1); // Pause on frame change
    }
    var num_keyframes_current_render;
    if (renderNumber == 42 || renderNumber == 56) {
      num_keyframes_current_render = configuration.num_key_frames;
    }
    if (renderNumber == 14) {
      num_keyframes_current_render = configuration.num_key_frames_render14;
    }
    if (frameNum == 0) {
      setFrameNum(num_keyframes_current_render - 1);
      setLeadingFrameNum(num_keyframes_current_render - 1);
    } else {
      setFrameNum((frameNum - 1) % num_keyframes_current_render);
      setLeadingFrameNum((frameNum - 1) % num_keyframes_current_render);
    }
  }, [isPaused, frameNum, renderNumber]);

  // This function is called when the user clicks the play/pause button
  const toggleMainTLPlay = useCallback(() => {
    var num_keyframes_current_render;
    var duration; 
    if(renderNumber == 42 || renderNumber == 56){
      num_keyframes_current_render = configuration.num_key_frames;
      duration = 10000;
    }
    if(renderNumber == 14){
      num_keyframes_current_render = configuration.num_key_frames_render14;
      duration = 6400;
    } 

    // Has the animation started?
    let msElapsed = animationStartTime
      ? (Date.now() - animationStartTime) % duration // There are 10000 ms in the duration of the animation
      : 0;

    // If the animation is playing
    if (!isPaused) {
      // Does main.current exists and is it visible?
      if (mainTL.current && mainTL.current.options.opacity === 1) {
        setFrameNum(
          Math.round((num_keyframes_current_render * msElapsed) / duration) %
            num_keyframes_current_render
        );
      }

      sync_flag.current = 0; // Sync_flag says whether the animation is synced
      let loading = document.getElementsByClassName('loading')[0];
      if (loading) {
        loading.style.display = 'none';
      }
    } else {
      setLeadingFrameNum(-1); // Turns off leading frame: see MyMap.js
    }
    setIsPaused(!isPaused);
  }, [isPaused, frameNum, mainTL, sync_flag, animationStartTime]);

  useEffect(() => {
    const sidebar = document.getElementsByClassName('sidebar')[0];

    const handleMouseOver = () => setMouseOverSidebar(true);
    const handleMouseOut = () => setMouseOverSidebar(false);

    if (sidebar) {
      sidebar.addEventListener('mouseover', handleMouseOver);
      sidebar.addEventListener('mouseout', handleMouseOut);
    }

    return () => {
      if (sidebar) {
        sidebar.removeEventListener('mouseover', handleMouseOver);
        sidebar.removeEventListener('mouseout', handleMouseOut);
      }
    };
  }, [sidebarState]);

  useMapEvents(
    {
      // Updates the current mouse location
      mousemove: (e) => {
        setCurrentCoords(map.project(e.latlng, 7));
        let coords = map.project(e.latlng, 7);
        var sidebar = document.getElementsByClassName('sidebar')[0];
        if (sidebar) {
          let sidebarRect = sidebar.getBoundingClientRect();
          if (
            (coords.x > configuration.map_width ||
              coords.x < 0 ||
              coords.y > configuration.map_height ||
              coords.y < 0) && // mouse is not on the map
            !mouseOverSidebar &&
            tilePreview == -1
          ) {
            console.log('here' + Math.random());
            sidebar.style.animation = 'fadeSidebarOut 0.35s';
            setTimeout(() => {
              setSidebar(0);
            }, 340);

            setSidebar(0);
            var urlId = '/todem/';
            window.history.pushState('', '', urlId);
            var newMasterTile = {
              masterTile: {
                name: 'Click on a tile!',
                tokenId: 'NaN',
                sentimentX_token: '',
                sentimentY_token: '',
                img_prompt: '',
                wiki_token: '',
                rgb: '',
                dimensions: ['', ''],
              },
            };
            setMasterTile(newMasterTile);
            setSelectedSubregionNull();
            setSubregionMode(-1);
            setCurrentPrompt(null);
            setTilePreview(-1);
          }
        }
      },
    },
    [mouseOverSidebar]
  );

  return (
    <div className="bottombar">
      {window.innerWidth >= 600 ? (
        <>
          <div
            className="bttn fullscreen-btn"
            style={{
              top: 8,
              backgroundColor: handle.active ? '#bl201634' : '#201634',
            }}
            onClick={() => {
              if (handle.active) {
                handle.exit();
              } else {
                handle.enter();
              }
            }}
          >
            {!handle.active ? (
              <BiFullscreen
                className="icon"
                style={{
                  fontSize: 18,
                }}
              />
            ) : (
              <MdOutlineFullscreenExit
                className="icon"
                style={{
                  fontSize: 25,
                }}
              />
            )}
            <span
              className="tooltiptext tooltiptext-fs"
              style={{
                backgroundColor: configuration.tooltip_background_color,
                color: configuration.tooltip_text_color,
              }}
            >
              fullscreen
            </span>
          </div>

          <div className="bttn zoom-display unselectable">
            {currentCoords.x < configuration.map_width &&
            currentCoords.x > 0 &&
            currentCoords.y < configuration.map_height &&
            currentCoords.y > 0 ? (
              <span>
                <span style={{ color: '#9595ff' }}>x:</span>
                {currentCoords.x.toFixed(0)}
                <span style={{ color: '#9595ff' }}> y:</span>
                {configuration.map_height - currentCoords.y.toFixed(0)}
                <span style={{ color: '#9595ff' }}> z:</span>
              </span>
            ) : (
              <span>
                <span style={{ color: 'rgb(79, 79, 255)' }}>x:</span> –{' '}
                <span style={{ color: 'rgb(79, 79, 255)' }}>y:</span> –{' '}
                <span style={{ color: 'rgb(79, 79, 255)' }}>z:</span>&nbsp;
              </span>
            )}
            <span>
              {(map.getZoom() - 0.3).toFixed(1)}
              <span style={{ color: '#9595ff' }}> f:</span>
              {isPaused
                ? (frameNum * 8.33).toFixed(0)
                : liveFrameNum.toFixed(0)}
            </span>
          </div>

          <div className="bttn frame-controller">
            <AiFillStepBackward
              onClick={() => {
                frameBackward();
              }}
              className="icon"
              style={{ fontSize: 35, pointerEvents: 'all', marginRight: 10 }}
            />

            {isPaused ? (
              <BsPlayFill
                className="icon"
                onClick={() => {
                  toggleMainTLPlay();
                }}
                style={{ fontSize: 45, pointerEvents: 'all' }}
              />
            ) : (
              <BsPauseFill
                className="icon"
                onClick={() => {
                  toggleMainTLPlay();
                }}
                style={{ fontSize: 45, pointerEvents: 'all' }}
              />
            )}

            <AiFillStepForward
              onClick={() => {
                frameForward();
              }}
              className="icon"
              style={{ fontSize: 35, pointerEvents: 'all', marginLeft: 10 }}
            />
          </div>

          {/* {!isPaused ? (
            <div
              className="bttn loading"
              style={{
                marginLeft: '-15px',
                left: '50vw',
                top: '-3.5em',
                backgroundColor: '#bl201634',
                position: 'absolute',
              }}
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
                fill="white"
              >
                <rect
                  className="spinner_9y7u"
                  x="1"
                  y="1"
                  rx="1"
                  width="10"
                  height="10"
                />
                <rect
                  className="spinner_9y7u spinner_DF2s"
                  x="1"
                  y="1"
                  rx="1"
                  width="10"
                  height="10"
                />
                <rect
                  className="spinner_9y7u spinner_q27e"
                  x="1"
                  y="1"
                  rx="1"
                  width="10"
                  height="10"
                />
              </svg>
            </div>
          ) : null} */}

          <div
            className="bttn recenter"
            style={{
              top: 8,
              backgroundColor: handle.active ? '#bl201634' : '#201634',
            }}
            onClick={() => {
              setSidebar(0);
              setListView(0);

              let centerpoint = calculateCenter();
              let new_zoom = fillScreen();
              if (
                map.getCenter() !== centerpoint &&
                map.getZoom() !== new_zoom
              ) {
                map.flyTo(centerpoint, new_zoom, {
                  animate: 1,
                  easeLinearity: 0,
                });
              }
              var urlId = '/todem/';
              window.history.pushState('', '', urlId);
              var newMasterTile = {
                masterTile: {
                  name: 'Click on a tile!',
                  tokenId: 'NaN',
                  sentimentX_token: '',
                  sentimentY_token: '',
                  img_prompt: '',
                  wiki_token: '',
                  rgb: '',
                  dimensions: ['', ''],
                },
              };
              setMasterTile(newMasterTile);
              setSelectedSubregionNull();
              setSubregionMode(-1);
              setCurrentPrompt(null);
              setTilePreview(-1);
            }}
          >
            <MdOutlineCenterFocusStrong
              className="icon"
              style={{ fontSize: 17 }}
            />

            <span
              className="tooltiptext tooltiptext-recenter "
              style={{
                backgroundColor: configuration.tooltip_background_color,
                color: configuration.tooltip_text_color,
              }}
            >
              recenter
            </span>
          </div>
        </>
      ) : null}

      <FrameCounter
        animationStartTime={animationStartTime}
        setLiveFrameNum={setLiveFrameNum}
        mainTL={mainTL}
        isPaused={isPaused}
        renderNumber={renderNumber}
      />
    </div>
  );
};
export default Bottombar;
